import { useT } from '../../i18n';
import type { AccountSignInSession } from '../../lib/api/accountSignIn';
import { AccountCodeSignIn } from './AccountCodeSignIn';
import { PasskeySignInAction, type PasskeySignInFailure } from './PasskeySignInAction';
import { Padel24Logo } from '../ui';
import { PORTAL_BRAND } from '../../data/brand';

export interface LoginScreenProps {
  /** Attempt a login. Returns `{ success }` (sync or async); on failure carries an `error`. */
  /**
   * Why the session ended, as a CODE — never a sentence, for the same reason.
   * The screen decides what it says, and in which language.
   */
  notice?: 'session_expired';
  /**
   * Open the e-mail-code sign-in screen (#679).
   *
   * Optional for `onForgotPassword`'s reason, which is worth repeating because
   * it is the whole gating rule: absent on backends that have no code sign-in to
   * offer, and then the link is not rendered at all. An offer that cannot be
   * honoured is worse than no offer.
   */
  /**
   * The customer sign-in completed, with the session the door issued (#837
   * review).
   *
   * It used to be `onSignInWithCode: () => void` — «open the other screen» —
   * and the screen it opened asked `/auth/club/otp`, which issues a code ONLY
   * for an existing active local identity. A new player's address got the
   * non-enumerating 202 and no code: a dead step at the product's front door.
   *
   * The flow is inline now, against `/auth/otp`, which provisions for an
   * unknown address. Presence of this callback is also what says «this
   * deployment has a customer sign-in», which is the condition the layout
   * turns on.
   */
  onSignedInWithCode: (session: AccountSignInSession, email: string) => void;
  /**
   * Run a passkey sign-in. Resolves to `null` on success — the gate takes over
   * and this screen goes away — or to a FAILURE CODE.
   *
   * A CODE, NEVER A SENTENCE, for the same reason `notice` above is one: the
   * screen decides what it says and in which language, and the gate has no
   * catalog. The first version of this returned a translated string, which put
   * Norwegian in the one component that cannot translate it.
   *
   * UNLIKE the other two offers this is not «open a screen» — a passkey needs no
   * form, so the ceremony happens from here. Absent when the backend has no
   * passkey door or the browser has no WebAuthn, and then nothing is rendered.
   */
  onSignInWithPasskey?: () => Promise<PasskeySignInFailure | null>;
}

// The failure type lives with the action that renders it, and is re-exported
// here because the gate and the tests have always imported it from this file.
export type { PasskeySignInFailure } from './PasskeySignInAction';

/**
 * Unauthenticated customer sign-in: an e-mail address, then a one-time code,
 * with a passkey as the optional secondary door.
 * Extracted verbatim from the monolith (legacy/App.tsx).
 *
 * The username/password form is the LEGACY shared-credential gate and is
 * client-side only (docs/architecture/SECURITY.md §4e). It stands in for the club-user
 * account system that does not exist yet, and is deliberately left in place.
 *
 * Starpoint STAFF do not sign in here. Entra sign-in lives on /sa, the internal
 * control panel — club administrators are customers, not directory members, so
 * offering them a Microsoft button on this screen was misleading.
 */
export function LoginScreen({ notice, onSignedInWithCode, onSignInWithPasskey }: LoginScreenProps) {
  // ── THE ORDER OF THE DOORS IS THE POINT (#695) ─────────────────────
  //
  // Where this deployment HAS a passkey door and the browser can open it, the
  // passkey is the FIRST and PRIMARY action, the one-time code is the obvious
  // fallback beside it, and the password form is put away behind a deliberate
  // choice.
  //
  // THAT IS NOT COSMETICS. #695 is Phase B moving toward Phase C, and a screen
  // that opens with an address field, a password field and a primary password
  // button leaves the OLD model as the default no matter what else it offers
  // underneath. Somebody holding a passkey must not be led into typing a
  // password first.
  //
  // Password stays REACHABLE, because it must: existing LOCAL accounts still
  // need a first-contact path, and retiring it is a later phase with its own
  // criteria. Demoted, not removed.
  //
  // WITHOUT a passkey door nothing moves — the password form is the first thing
  // on the screen, autofocused, exactly as it was. There is nothing better to
  // lead with, and a layout that pretends otherwise would be a dead offer.
  //
  // ── AND THIS ARRANGEMENT IS TRANSITIONAL (#751) ────────────────────
  //
  // The owner decided on 2026-10-12, reaffirmed on 2026-10-14 when #704 and
  // #751 were reconciled, that the DEFAULT sign-in becomes one e-mail field
  // and a one-time code, with passkeys optional and secondary. That is the
  // opposite of the order above, and it is the approved direction —
  // `docs/auth/PASSWORDLESS.md` is titled for it.
  //
  // Stated here because the styling of this screen has been refreshed twice
  // around the current order, and visual investment is the quiet way an
  // interim arrangement turns into a contract nobody remembers choosing. The
  // order below is what the product does TODAY; it is not the model, and
  // moving it is #751's work rather than a thing to be preserved.
  // ── WHICH DOOR LEADS (#751) ──────────────────────────────────────
  //
  // The e-mail code, wherever this deployment has one. It replaced
  // `passkeyFirst`, and the rename is the decision: «is there a passkey» is no
  // longer the question that decides the layout, because the passkey is no
  // longer what leads.
  // ── THE SCREEN DOES NOT ENUMERATE ITS DOORS AT REST (#704) ───────
  //
  // «Here are our auth mechanisms, hack us» (Jens, 2026-10-08). A signed-out
  // page that lists passkey, one-time code and password tells an unauthenticated
  // visitor exactly which mechanisms exist and invites them to pick the weakest —
  // and it advertises the password door as a first-class choice to the people we
  // are trying to move OFF it.
  //
  // BE HONEST ABOUT WHAT THIS BUYS. Nothing here is hidden from somebody who
  // clicks, and it is not a control: the doors are the same doors. What changes
  // is that the strongest one is the only thing offered until a person asks for
  // something else, which lowers the reconnaissance signal and stops nudging
  // ordinary users toward the transitional path.
  //
  // ── THE PASSKEY ACTION IS SHARED (player-app passkeys) ───────────
  //
  // The disclosure, the button and the failure sentences moved to
  // `PasskeySignInAction`, so the player app's entrance offers the same door
  // rather than a copy of it. The rendered output here is unchanged, and
  // `components-login-passkey.test.tsx` passing untouched is the evidence.
  const t = useT();

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      // THE BRAND PURPLE, WITH DEPTH. A flat #6b21a8 read as a placeholder; a
      // gradient with a soft highlight behind the card reads as a surface.
      style={{
        background:
          'radial-gradient(60% 50% at 50% 0%, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 70%), linear-gradient(165deg, #4c1d95 0%, #6b21a8 55%, #7e22ce 100%)',
      }}
    >
      {/* PARKED WITH THE HEADER'S (#533). There is no signed-in account on
          this screen, so «only for one test account» cannot be expressed
          here at all — it is removed outright rather than gated on nothing.
          That is the one place the parking is not reversible by flipping a
          condition, and it is why the note lives here too. */}
      <div className="w-full max-w-sm">
        {/* Logo + tittel — the white wordmark on the purple, not the dark one. */}
        <div className="text-center mb-7">
          <div className="flex justify-center mb-3">
            <Padel24Logo size="xl" variant="full" tone="light" />
          </div>
          {/* LEFT AT 75% ON PURPOSE. Same floor, same background, but this
              one already clears it: 4.54:1 at the gradient's lightest stop,
              and 5.08:1 where the tagline actually sits, under the radial
              highlight at the top. Nudging it would be my taste rather than
              the standard, and the standard is the only mandate here. */}
          <p className="text-sm text-white/75">{t('app.tagline')}</p>
        </div>

        {/* Login-kort */}
        <div className="rounded-2xl bg-paper p-7 shadow-[0_24px_60px_-20px_rgba(20,10,40,0.6)]">
          {/* ── ONE HEADING, NOT TWO (#837 review, browser pass) ─────
              //
              // `AccountCodeSignIn` carries its own, and it has to: it changes
              // between the steps («Logg inn» → «Vi har sendt deg en kode»),
              // which a caller cannot do. Rendering this one as well stacked
              // «Logg inn» above «Logg inn» — visible the moment the screen
              // was looked at, and invisible to every test.
              //
              // The portal's identity is not lost with it: the wordmark, the
              // tagline and the footer are all on this screen already. */}
          <h2 className="font-display text-2xl font-semibold text-ink">{t('auth.login.title')}</h2>
          <p className="mt-1 mb-5 text-sm text-ink-3">{t('auth.login.lead')}</p>

          {notice && (
            <div className="mb-4 text-xs rounded-lg px-3 py-2 bg-amber-50 border border-amber-200 text-amber-900">
              {t('auth.login.sessionExpired')}
            </div>
          )}

          {/* ── ONE DOOR, AND IT IS THE ONLY ONE (#754) ───────────────
              //
              // This was a ternary: an e-mail code where the deployment had
              // one, and a password form where it did not. The password form
              // is gone — the credential is retired, no API accepts it, and a
              // fallback nothing can render is not a fallback.
              //
              // `onSignedInWithCode` is required now rather than optional,
              // which is the type system saying the same thing: a login
              // screen without a way to sign in is not a state this component
              // has. */}
          <div className="space-y-3">
            {/* ── THE E-MAIL FIELD, AT REST (#837 review) ──────────
                //
                // `docs/auth/PASSWORDLESS.md` slice 6: «One e-mail field at
                // rest enumerates nothing and is OTP-first». The first
                // version of this made a BUTTON primary and opened a
                // separate address screen — which reversed the old
                // hierarchy without implementing the agreed entrance, and
                // pointed at the wrong endpoint on the way.
                //
                // Shared with the player screen rather than reimplemented:
                // same two steps, same `/auth/otp`, same session. */}
            <AccountCodeSignIn onSignedIn={onSignedInWithCode} />
            {/* ── A DISCLOSURE THAT ACTUALLY CLOSES (#830 review) ──
                //
                // The old one rendered only while shut and called
                // `setOtherWaysOpen(true)` from two places and `false` from
                // none: a one-way door wearing a toggle's chevron, which
                // took its own label away with it and left a bare rule over
                // two unexplained buttons. It is a real toggle now, the
                // label stays, and `aria-expanded` says which way it is. */}
            {onSignInWithPasskey && (
              <PasskeySignInAction onSignInWithPasskey={onSignInWithPasskey} />
            )}
          </div>
        </div>

        {/* `white/80`, not `/60`: on the lightest point of the gradient
            (#7e22ce) 60% white measures 3.41:1 and 80% measures 4.97:1, so
            only one of the two clears the 4.5:1 floor for 11px text (#781).
            The flat-purple version this replaced was `text-ink-3` at 2.78:1,
            so the refresh improved it — just not far enough. */}
        <p className="text-center text-[11px] text-white/80 mt-6">
          {t('app.footer', { brand: PORTAL_BRAND.name })}
        </p>
      </div>
    </div>
  );
}
